import React, {
  useEffect, useId, useRef,
} from 'react';
import Button from '../../components/buttons/baseButton';
import customIcons from '../../utils/icons';
import { KeyCode } from '../../utils/constants';

export const CardLayoutSize = {
  Auto: 'auto',
  Fit: 'fit',
  Small: 'sm',
  Medium: 'md',
  Large: 'lg',
  Full: 'full',
} as const;

export type CardLayoutSizeValue = typeof CardLayoutSize[keyof typeof CardLayoutSize];

// Record makes every size need an entry, so a missing one fails the type check.
const sizeClasses: Record<CardLayoutSizeValue, string> = {
  [CardLayoutSize.Auto]: 'w-fit min-w-64 max-w-sm', // fits the content, between 256px and 384px
  [CardLayoutSize.Fit]: 'w-fit', // as wide as the content, no limits
  [CardLayoutSize.Small]: 'w-64', // 256px
  [CardLayoutSize.Medium]: 'w-80', // 320px
  [CardLayoutSize.Large]: 'w-96', // 384px
  [CardLayoutSize.Full]: 'w-full', // fills the parent
};

export interface CardLayoutProps {
  title?: string,
  headerContent?: React.ReactNode, // shown under the title, laid out in a row
  children: React.ReactNode,
  footer?: React.ReactNode,
  showFooterDivider?: boolean,
  onClose?: () => void,
  closeLabel?: string,
  size?: CardLayoutSizeValue,
  className?: string,
}

// Card frame with header, content and footer slots. Display only: no dialog, portal or positioning,
// so the caller decides where it sits, what closing does and what role it has.
export default function CardLayout({
  title,
  headerContent,
  children,
  footer,
  showFooterDivider = true,
  onClose,
  closeLabel = 'Close',
  size = CardLayoutSize.Auto,
  className = '',
}: Readonly<CardLayoutProps>) {
  const closeId = useId();
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!onClose) return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.key as KeyCode) === KeyCode.Escape && cardRef.current?.contains(event.target as Node)) {
        event.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const hasTitleRow = Boolean(title) || Boolean(onClose);
  const hasHeader = hasTitleRow || Boolean(headerContent);

  return (
    <div ref={cardRef} className={`flex flex-col ${sizeClasses[size]} text-left bg-white border border-pipgrey-border rounded-lg shadow-lg ${className}`}>
      {hasHeader && (
        <div className="mx-3 py-2 border-b border-pipbg-grouped">
          {hasTitleRow && (
            <div className="flex items-center gap-2">
              {title && <h2 className="flex-1 min-w-0 text-body-large text-piptext-strong">{title}</h2>}
              {onClose && (
                <Button
                  id={closeId}
                  icon={customIcons.closeIcon}
                  ariaLabel={closeLabel}
                  onClick={onClose}
                  className="shrink-0 ml-auto size-8 p-0 rounded text-base text-piptext-body hover:bg-pipbg-subtle"
                />
              )}
            </div>
          )}
          {headerContent && <div className="flex items-center gap-2 mt-1">{headerContent}</div>}
        </div>
      )}
      <div className="px-3 py-2">{children}</div>
      {footer && (
        <div className={`flex justify-end mx-3 py-3 ${showFooterDivider ? 'border-t border-pipbg-grouped' : ''}`}>
          {footer}
        </div>
      )}
    </div>
  );
}
